import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import PeopleIcon from '@mui/icons-material/People';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import CloudIcon from '@mui/icons-material/Cloud';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BusinessIcon from '@mui/icons-material/Business';
import LoginIcon from '@mui/icons-material/Login';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from '../api/axios';

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get('/subscription-plans');
      setPlans(response.data);
    } catch (err) {
      console.error('Failed to load plans', err);
    }
  };

  const features = [
    {
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      title: 'Real-time Code Collaboration',
      description: 'Conduct live coding interviews with real-time code editing and syntax highlighting for multiple programming languages.',
      color: '#1976d2'
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      title: 'Multi-User Support',
      description: 'Invite multiple interviewers to collaborate and evaluate candidates together in the same session.',
      color: '#f5576c'
    },
    {
      icon: <VideoCallIcon sx={{ fontSize: 40 }} />,
      title: 'Seamless Interview Flow',
      description: 'Share interview links instantly with candidates. No installation required - just click and start coding.',
      color: '#43e97b'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Secure & Private',
      description: 'Enterprise-grade security with encrypted connections and private session rooms for your interviews.',
      color: '#667eea'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Lightning Fast',
      description: 'Low-latency real-time updates ensure smooth collaboration without any lag or delays.',
      color: '#f093fb'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40 }} />,
      title: 'Session Management',
      description: 'Track all your interviews, review session history, and manage your interviewing team from one dashboard.',
      color: '#4facfe'
    }
  ];

  const benefits = [
    'Conduct unlimited coding interviews',
    'Real-time collaborative code editor',
    'Syntax highlighting for 50+ languages',
    'Instant candidate link sharing',
    'Session history and analytics',
    'Team member management',
    'No software installation required',
    'Mobile-responsive interface',
    '24/7 platform availability',
    'Regular feature updates'
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <AppBar position="static" elevation={0} sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <BusinessIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Code Interview Platform
          </Typography>
          <Button
            color="inherit"
            startIcon={<LoginIcon />}
            onClick={() => navigate('/login')}
            sx={{ mr: 1 }}
          >
            Login
          </Button>
          <Button
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255,255,255,0.1)'
              }
            }}
            onClick={() => navigate('/register')}
          >
            Sign Up Free
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="🚀 Launch Your Interviews Today"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  mb: 2,
                  fontWeight: 600
                }}
              />
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                Conduct Technical Interviews Like a Pro
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontWeight: 400,
                  lineHeight: 1.6
                }}
              >
                The ultimate platform for real-time coding interviews. Collaborate with candidates,
                evaluate skills, and hire the best talent faster.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/register')}
                  sx={{
                    bgcolor: 'white',
                    color: '#667eea',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#f5f5f5',
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    },
                    transition: 'all 0.3s'
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  Sign In
                </Button>
              </Box>
              <Box sx={{ mt: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>1000+</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Interviews Conducted</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>500+</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Happy Companies</Typography>
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>50+</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Programming Languages</Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={20}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#667eea', mr: 2 }}>
                    <CodeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                      Live Interview Session
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Real-time collaborative coding
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    bgcolor: '#1e1e1e',
                    color: '#d4d4d4',
                    p: 2,
                    borderRadius: 2,
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    lineHeight: 1.6
                  }}
                >
                  <Box sx={{ color: '#569cd6' }}>function</Box>
                  <Box sx={{ ml: 2 }}>
                    <span style={{ color: '#dcdcaa' }}>reverseString</span>
                    <span style={{ color: '#d4d4d4' }}>(</span>
                    <span style={{ color: '#9cdcfe' }}>str</span>
                    <span style={{ color: '#d4d4d4' }}>) {'{'}</span>
                  </Box>
                  <Box sx={{ ml: 4, color: '#c586c0' }}>return</Box>
                  <Box sx={{ ml: 6 }}>
                    <span style={{ color: '#9cdcfe' }}>str</span>
                    <span style={{ color: '#d4d4d4' }}>.</span>
                    <span style={{ color: '#dcdcaa' }}>split</span>
                    <span style={{ color: '#d4d4d4' }}>(</span>
                    <span style={{ color: '#ce9178' }}>''</span>
                    <span style={{ color: '#d4d4d4' }}>)</span>
                  </Box>
                  <Box sx={{ ml: 8 }}>
                    <span style={{ color: '#d4d4d4' }}>.</span>
                    <span style={{ color: '#dcdcaa' }}>reverse</span>
                    <span style={{ color: '#d4d4d4' }}>()</span>
                  </Box>
                  <Box sx={{ ml: 8 }}>
                    <span style={{ color: '#d4d4d4' }}>.</span>
                    <span style={{ color: '#dcdcaa' }}>join</span>
                    <span style={{ color: '#d4d4d4' }}>(</span>
                    <span style={{ color: '#ce9178' }}>''</span>
                    <span style={{ color: '#d4d4d4' }}>);</span>
                  </Box>
                  <Box sx={{ ml: 2, color: '#d4d4d4' }}>{'}'}</Box>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>I</Avatar>
                  <Typography variant="caption" color="text.secondary">
                    Interviewer is typing...
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip label="Live" color="success" size="small" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>
            Everything You Need for Technical Interviews
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Powerful features designed to streamline your hiring process
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 6
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: feature.color,
                      width: 64,
                      height: 64,
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3, color: '#1a237e' }}>
                Why Choose Our Platform?
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Built by developers for developers, our platform offers the most intuitive and
                powerful solution for conducting technical interviews.
              </Typography>
              <List>
                {benefits.slice(0, 5).map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#43e97b' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                {benefits.slice(5).map((benefit, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: '#43e97b' }} />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#1a237e' }}>
            Simple, Transparent Pricing
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Choose the plan that fits your needs. No hidden fees.
          </Typography>
        </Box>

        {plans.length > 0 ? (
          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} key={plan.id}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.features?.popular ? '3px solid #667eea' : '1px solid #e0e0e0',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8
                    }
                  }}
                >
                  {plan.features?.popular && (
                    <Chip
                      label="MOST POPULAR"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        fontWeight: 600
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {plan.description}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#667eea' }}>
                        ${plan.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        per {plan.billing_cycle}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ textAlign: 'left', mb: 3 }}>
                      <List dense>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${plan.max_concurrent_sessions} concurrent sessions`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Real-time collaboration"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Session history"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Team management"
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                    <Button
                      variant={plan.features?.popular ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      onClick={() => navigate('/register')}
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Loading pricing plans...
            </Typography>
          </Box>
        )}
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <TrendingUpIcon sx={{ fontSize: 60, mb: 2, opacity: 0.9 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to Transform Your Hiring?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.95 }}>
              Join hundreds of companies using our platform to hire top technical talent
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/register')}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                },
                transition: 'all 0.3s'
              }}
            >
              Start Free Trial
            </Button>
            <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
              No credit card required • Setup in 2 minutes
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a237e', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Code Interview Platform
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                The ultimate platform for conducting technical interviews with real-time collaboration.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  color="inherit"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/login')}
                >
                  Login
                </Button>
                <Button
                  color="inherit"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => navigate('/register')}
                >
                  Sign Up
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Contact
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Have questions? Reach out to us at:
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                support@smartdataiq.com
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" sx={{ textAlign: 'center', opacity: 0.7 }}>
            © {new Date().getFullYear()} Smart Data IQ LLC. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

